
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, UserPlus, Users } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface AccountLimitsProps {
  settings: {
    maxAdminAccounts: number;
    maxUserAccounts: number;
  };
  isSaving: boolean;
  updateLimits: (maxAdmins: number, maxUsers: number) => Promise<boolean>;
}

const AccountLimits: React.FC<AccountLimitsProps> = ({
  settings,
  isSaving,
  updateLimits
}) => {
  const { toast } = useToast();
  const [maxAdmins, setMaxAdmins] = useState<number | ''>('');
  const [maxUsers, setMaxUsers] = useState<number | ''>('');

  // Load current values when settings are loaded
  useEffect(() => {
    if (settings) {
      setMaxAdmins(settings.maxAdminAccounts);
      setMaxUsers(settings.maxUserAccounts);
    }
  }, [settings]);

  const handleSaveLimits = async () => {
    if (maxAdmins === '' || maxUsers === '') {
      toast({
        title: "Error",
        description: "Please enter valid numbers for account limits",
        variant: "destructive",
      });
      return;
    }
    
    // Convert to numbers and validate
    const adminLimit = Number(maxAdmins);
    const userLimit = Number(maxUsers);
    
    if (adminLimit < 1 || userLimit < 1) {
      toast({
        title: "Invalid Limits",
        description: "Account limits must be at least 1",
        variant: "destructive",
      });
      return;
    }
    
    await updateLimits(adminLimit, userLimit);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Users className="mr-2 h-5 w-5" />
          Account Limits
        </CardTitle>
        <CardDescription>
          Set the maximum number of admin and user accounts allowed for this instance
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="max-admins" className="flex items-center">
              <Shield className="mr-2 h-4 w-4" />
              Maximum Admin Accounts
            </Label>
            <Input
              id="max-admins"
              type="number"
              min="1"
              value={maxAdmins}
              onChange={(e) => setMaxAdmins(e.target.value === '' ? '' : parseInt(e.target.value))}
              disabled={isSaving}
            />
            <p className="text-xs text-gray-500">
              Current usage: 1 of {settings.maxAdminAccounts}
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="max-users" className="flex items-center">
              <UserPlus className="mr-2 h-4 w-4" />
              Maximum User Accounts
            </Label>
            <Input
              id="max-users"
              type="number"
              min="1"
              value={maxUsers}
              onChange={(e) => setMaxUsers(e.target.value === '' ? '' : parseInt(e.target.value))}
              disabled={isSaving}
            />
            <p className="text-xs text-gray-500">
              Current usage: 24 of {settings.maxUserAccounts}
            </p>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button 
          onClick={handleSaveLimits} 
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Limits'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default AccountLimits;
